// ============================================================================
// navigationConfig.js — THE single navigation source of truth.
// ============================================================================
// Consumed by:
//   1. App.jsx        — sidebar, search, navigation metadata
//   2. HomeLaunchpad.jsx — Launch Pad
//   3. Any future Module Landing Pages (e.g. Finance)
// No other file should define a separate NAV / NAV_GROUPS array.
//
// Grouping/labels are taken from HomeLaunchpad.jsx's NAV_GROUPS (the more
// recently curated classification) rather than App.jsx's NAV, which had a
// few differing group assignments for the same pages (Cost Assessment,
// PO Reconciliation). Per the approved design: "no module moves between
// groups" -- this file simply picks one existing classification as
// authoritative rather than inventing a new one.
//
// `insideTabs` is populated ONLY for the 5 modules confirmed complex in the
// forensic audit (2026-08-13) and approved in the design phase:
//   - Purchase Orders (3 tabs)   - Finance (8 tabs)
//   - Store (5 tabs)             - Cost Control (2 tabs)
//   - Inventory Management (4 tabs)
// Every other module has insideTabs: [] -- verified from the audit, not
// assumed. Do not add insideTabs to any other module without re-confirming
// its internal tab structure in code first.
// ============================================================================

export const NAV_GROUPS = [
  { id: 'portfolio', label: 'Portfolio', items: [
    { label: 'Dashboard',          sub: 'KPIs, EVM, S-Curve',                 page: 'dashboard',   insideTabs: [] },
    { label: 'Projects',           sub: 'Manage active projects',             page: 'projects',    insideTabs: [] },
    { label: 'Organizations',      sub: 'Company entities & hierarchy',       page: 'organizations', insideTabs: [] },
    { label: 'My Workspace',       sub: 'Your tasks and recent activity',     page: 'workspace',   insideTabs: [] },
    // First integration slice: Estimation is mounted as its own route tree
    // under /estimation/*, with its own AuthProvider/pages untouched. This
    // is the only new NAV_GROUPS entry added for it.
    { label: 'Estimation',         sub: 'Tender pricing & cost estimation',   page: 'estimation',  insideTabs: [] },
  ]},
  { id: 'setup', label: 'Setup', items: [
    { label: 'Cost Codes',         sub: 'Code hierarchy — Direct & Indirect', page: 'cost-codes',  insideTabs: [] },
    { label: 'Users & Roles',      sub: 'Accounts, permissions & access',     page: 'users',       insideTabs: [] },
    { label: 'Permissions',        sub: 'Role-based access control',          page: 'permissions', insideTabs: [] },
    { label: 'Currency & Exchange Rates', sub: 'Currency master & FX rate history', page: 'currency-fx', insideTabs: [] },
    { label: 'Subscription Management', sub: 'Organization subscriptions & billing', page: 'subscription-management', insideTabs: [] },
  ]},
  { id: 'contract', label: 'Contract & BOQ', items: [
    { label: 'BOQ',                sub: 'Bill of Quantities — selling prices', page: 'boq',           insideTabs: [] },
    { label: 'Change Register',    sub: 'Variation orders & technical changes', page: 'change-register', insideTabs: [] },
    { label: 'EOT Register',       sub: 'Extension of time claims',           page: 'eot-register',  insideTabs: [] },
    { label: 'Risk Register',      sub: 'Project risks & mitigation',         page: 'risk-register', insideTabs: [] },
  ]},
  { id: 'budget', label: 'Budget', items: [
    { label: 'Direct Budget',      sub: 'Packages & sub code budgets',        page: 'budget',         insideTabs: [] },
    { label: 'Indirect Budget',    sub: 'Preliminaries & site costs',         page: 'indirect',       insideTabs: [] },
    { label: 'Budget Summary',     sub: 'Direct + Indirect combined',         page: 'budget-summary', insideTabs: [] },
    { label: 'Scheduling',         sub: 'Cash in planning & progress',        page: 'scheduling',     insideTabs: [] },
    { label: 'Final Account',      sub: 'Contract closeout statement',        page: 'final-account',  insideTabs: [] },
  ]},
  { id: 'procurement', label: 'Procurement', items: [
    { label: 'Purchase Requisitions', sub: 'PR to PO workflow',               page: 'purchase-requisitions', insideTabs: [] },
    { label: 'RFQ & Quotations',   sub: 'Vendor quotation management',        page: 'rfq',            insideTabs: [] },
    {
      label: 'Purchase Orders', sub: 'Material POs, equipment POs & commitment search', page: 'purchase-orders',
      insideTabs: [
        { label: 'Material POs' },
        { label: 'Equipment POs' },
        { label: 'Search' },
      ],
    },
    {
      label: 'Store', sub: 'Receiving, issuing & stock transactions', page: 'store',
      insideTabs: [
        { label: 'Receive/Issue by PO' },
        { label: 'All Receipts' },
        { label: 'All Issues' },
        { label: 'Inventory' },
        { label: 'Suppliers' },
      ],
    },
    {
      label: 'Inventory Management', sub: 'Inventory overview, movements & inter-project transfers', page: 'inventory-management',
      insideTabs: [
        { label: 'Overview' },
        { label: 'Inventory' },
        { label: 'Movements' },
        { label: 'Transfers' },
      ],
    },
    { label: 'Material Master',    sub: 'Catalog and stock policy',           page: 'material-master', insideTabs: [] },
    { label: 'PO Reconciliation',  sub: 'PO vs GRN matching',                 page: 'po-reconciliation', insideTabs: [] },
    { label: 'Vendor Master',      sub: 'Approved vendor register',           page: 'vendor-master',  insideTabs: [] },
  ]},
  { id: 'subcontracts', label: 'Subcontracts', items: [
    { label: 'Subcontracts',       sub: 'Contract management & tracking',     page: 'subcontracts',   insideTabs: [] },
    { label: 'IPC Certificates',   sub: 'Interim payment certification',      page: 'ipc',            insideTabs: [] },
    { label: 'Retention Management', sub: 'Release schedule & tracking',      page: 'retention',      insideTabs: [] },
  ]},
  { id: 'finance', label: 'Cost & Finance', items: [
    { label: 'HR / Payroll',       sub: 'Staff costs & payroll entries',      page: 'payroll',        insideTabs: [] },
    {
      label: 'Finance', sub: 'Cash movement, planning & procurement-linked payments', page: 'finance',
      insideTabs: [
        { label: 'Cash In / Out' },
        { label: 'Cashflow' },
        { label: 'CF Expectation' },
        { label: 'CF Packages' },
        { label: 'Cash Out' },
        { label: 'Cash Out Forecast' },
        { label: 'Financial Fees' },
        { label: 'PO Lines' },
      ],
    },
    { label: 'Accrual',            sub: 'Pending cost provisions',            page: 'accrual',        insideTabs: [] },
    { label: 'Petty Cash',         sub: 'Voucher management',                 page: 'petty-cash',     insideTabs: [] },
    { label: 'Cost Assessment',    sub: 'Independent cost review',            page: 'cost-assessment', insideTabs: [] },
  ]},
  { id: 'reports', label: 'Forecasts & Reports', items: [
    { label: 'Direct Forecast',    sub: 'EAC — direct works packages',        page: 'direct-forecast', insideTabs: [] },
    { label: 'Indirect Forecast',  sub: 'EAC — indirect & prelims',           page: 'indirect-forecast', insideTabs: [] },
    { label: 'Releases',           sub: 'Approved project forecast history',  page: 'release-management', insideTabs: [] },
    {
      label: 'Cost Control', sub: 'Monthly actuals vs budget', page: 'costcontrol',
      insideTabs: [
        { label: 'Activity' },
        { label: 'Report' },
      ],
    },
    { label: 'Rate Analysis',      sub: 'Budget vs actual rate comparison',   page: 'rate-analysis',  insideTabs: [] },
    { label: 'Labour Productivity', sub: 'Output & productivity tracking',    page: 'productivity',   insideTabs: [] },
    { label: 'Equipment Productivity', sub: 'Equipment output tracking',      page: 'equipment-productivity', insideTabs: [] },
    { label: 'Alerts & Variances', sub: 'Risk intelligence & variances',      page: 'alerts',         insideTabs: [] },
    { label: 'Monthly Actual Cost', sub: 'Actual cost by period & source',    page: 'monthly-cost',   insideTabs: [] },
    { label: 'Analytics Studio',   sub: 'Custom charts & reports builder',    page: 'analytics-studio', insideTabs: [] },
    { label: 'Document Center',    sub: 'All project files in one place',     page: 'documents',      insideTabs: [] },
    { label: 'Document Governance', sub: 'Missing mandatory documents',       page: 'document-governance', insideTabs: [] },
  ]},
  { id: 'site-eng', label: 'Site & Engineering', items: [
    { label: 'Site Allocation Draft', sub: 'Code labor/equipment site entries', page: 'site-allocation', insideTabs: [] },
    { label: 'QS Quantity Tracking', sub: 'Monthly FQAC & progress',           page: 'qs-tracking',    insideTabs: [] },
    { label: 'Risks & Changes',    sub: 'Engineering change tracker',          page: 'risks-changes',  insideTabs: [] },
  ]},
]

// Flat lookup, derived from NAV_GROUPS -- used by App.jsx's sidebar/search,
// which needs a flat { key, label, group } shape rather than nested groups.
// Generated, not hand-maintained, so it can never drift from NAV_GROUPS.
export const NAV = NAV_GROUPS.flatMap(g =>
  g.items.map(item => ({ key: item.page, label: item.label, group: g.label, sub: item.sub, insideTabs: item.insideTabs, badge: item.badge }))
)

export const GROUPS = NAV_GROUPS.map(g => g.label)

// Short labels for the narrow 60px left rail in App.jsx. Fixes the
// previously-missing 'Site & Engineering' entry (forensic audit P0-4).
export const GROUP_LABELS = {
  'Portfolio': 'Home',
  'Setup': 'Setup',
  'Contract & BOQ': 'Contract',
  'Budget': 'Budget',
  'Procurement': 'Procure',
  'Subcontracts': 'Subcon',
  'Cost & Finance': 'Finance',
  'Forecasts & Reports': 'Reports',
  'Site & Engineering': 'Site & Eng',
  'Workflow': 'Workflow',
}
